import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AgentRegistrationForm = ({ onSubmit, isLoading, agentData }) => {
  const [formData, setFormData] = useState({
    agentType: agentData?.agentType || '',
    firstName: agentData?.firstName || '',
    lastName: agentData?.lastName || '',
    email: agentData?.email || '',
    phone: agentData?.phone || '',
    dateOfBirth: agentData?.dateOfBirth || '',
    nationality: agentData?.nationality || '',
    country: agentData?.country || '',
    address: agentData?.address || '',
    city: agentData?.city || '',
    postalCode: agentData?.postalCode || '',
    businessName: agentData?.businessName || '',
    businessType: agentData?.businessType || '',
    yearsInBusiness: agentData?.yearsInBusiness || '',
    expectedVolume: agentData?.expectedVolume || '',
    agreeToTerms: agentData?.agreeToTerms || false,
    agreeToPrivacy: agentData?.agreeToPrivacy || false,
    agreeToCompliance: agentData?.agreeToCompliance || false
  });

  const [errors, setErrors] = useState({});

  const agentTypeOptions = [
    { value: 'individual', label: 'Individual Agent' },
    { value: 'business', label: 'Business Affiliate' },
    { value: 'franchise', label: 'Franchise Partner' }
  ];

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'AU', label: 'Australia' },
    { value: 'CA', label: 'Canada' }
  ];

  const businessTypeOptions = [
    { value: 'sole-proprietorship', label: 'Sole Proprietorship' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'corporation', label: 'Corporation' },
    { value: 'llc', label: 'Limited Liability Company' },
    { value: 'money-service', label: 'Money Service Business' },
    { value: 'retail', label: 'Retail Business' },
    { value: 'other', label: 'Other' }
  ];

  const expectedVolumeOptions = [
    { value: 'low', label: 'Under $50K/month' },
    { value: 'medium', label: '$50K - $250K/month' },
    { value: 'high', label: '$250K - $1M/month' },
    { value: 'enterprise', label: 'Over $1M/month' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData?.agentType) newErrors.agentType = 'Agent type is required';
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData?.nationality) newErrors.nationality = 'Nationality is required';
    if (!formData?.country) newErrors.country = 'Country is required';
    if (!formData?.address?.trim()) newErrors.address = 'Address is required';
    if (!formData?.city?.trim()) newErrors.city = 'City is required';
    if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Postal code is required';

    // Business-specific validation
    if (formData?.agentType === 'business' || formData?.agentType === 'franchise') {
      if (!formData?.businessName?.trim()) newErrors.businessName = 'Business name is required';
      if (!formData?.businessType) newErrors.businessType = 'Business type is required';
      if (!formData?.yearsInBusiness?.trim()) newErrors.yearsInBusiness = 'Years in business is required';
    }

    if (!formData?.expectedVolume) newErrors.expectedVolume = 'Expected volume is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData?.email && !emailRegex?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
    if (formData?.phone && !phoneRegex?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Age validation (must be 18+)
    if (formData?.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today?.getFullYear() - birthDate?.getFullYear();
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    // Terms agreement validation
    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Agent Terms and Conditions';
    }

    if (!formData?.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the Privacy Policy';
    }

    if (!formData?.agreeToCompliance) {
      newErrors.agreeToCompliance = 'You must agree to compliance requirements';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const isBusinessAgent = formData?.agentType === 'business' || formData?.agentType === 'franchise';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Agent Type Selection */}
      <div className="space-y-4">
        <div className="text-center space-y-2 mb-6">
          <h2 className="text-2xl font-semibold text-foreground">Agent Information</h2>
          <p className="text-muted-foreground">
            Tell us about yourself and your business goals as an agent partner.
          </p>
        </div>

        <Select
          label="Agent Type"
          placeholder="Select agent type"
          options={agentTypeOptions}
          value={formData?.agentType}
          onChange={(value) => handleInputChange('agentType', value)}
          error={errors?.agentType}
          description="Choose the type that best describes your business model"
          required
        />

        {/* Agent Type Information Cards */}
        {formData?.agentType && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            {formData?.agentType === 'individual' && (
              <div>
                <h4 className="font-medium text-foreground">Individual Agent</h4>
                <p className="text-sm text-muted-foreground">
                  Perfect for individuals looking to earn commissions by referring customers. 
                  No business license required, lower volume expectations.
                </p>
              </div>
            )}
            {formData?.agentType === 'business' && (
              <div>
                <h4 className="font-medium text-foreground">Business Affiliate</h4>
                <p className="text-sm text-muted-foreground">
                  For established businesses wanting to offer money transfer services. 
                  Requires business license and higher volume commitments.
                </p>
              </div>
            )}
            {formData?.agentType === 'franchise' && (
              <div>
                <h4 className="font-medium text-foreground">Franchise Partner</h4>
                <p className="text-sm text-muted-foreground">
                  Exclusive territory rights with full franchise support. 
                  Highest commission rates and comprehensive training included.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="User" size={20} />
          <span>Personal Information</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your email address"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          description="This will be your primary contact for agent communications"
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          description="Include country code for international numbers"
          required
        />

        <Input
          label="Date of Birth"
          type="date"
          value={formData?.dateOfBirth}
          onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
          error={errors?.dateOfBirth}
          description="You must be at least 18 years old to become an agent"
          required
        />

        <Select
          label="Nationality"
          placeholder="Select your nationality"
          options={countryOptions}
          value={formData?.nationality}
          onChange={(value) => handleInputChange('nationality', value)}
          error={errors?.nationality}
          searchable
          required
        />
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="MapPin" size={20} />
          <span>Address Information</span>
        </h3>

        <Select
          label="Country"
          placeholder="Select your country"
          options={countryOptions}
          value={formData?.country}
          onChange={(value) => handleInputChange('country', value)}
          error={errors?.country}
          searchable
          required
        />

        <Input
          label="Street Address"
          type="text"
          placeholder="Enter your full address"
          value={formData?.address}
          onChange={(e) => handleInputChange('address', e?.target?.value)}
          error={errors?.address}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="City"
            type="text"
            placeholder="Enter your city"
            value={formData?.city}
            onChange={(e) => handleInputChange('city', e?.target?.value)}
            error={errors?.city}
            required
          />
          
          <Input
            label="Postal Code"
            type="text"
            placeholder="Enter postal code"
            value={formData?.postalCode}
            onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
            error={errors?.postalCode}
            required
          />
        </div>
      </div>

      {/* Business Information - Only show for business/franchise agents */}
      {isBusinessAgent && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Building2" size={20} />
            <span>Business Information</span>
          </h3>

          <Input
            label="Business Name"
            type="text"
            placeholder="Enter your business name"
            value={formData?.businessName}
            onChange={(e) => handleInputChange('businessName', e?.target?.value)}
            error={errors?.businessName}
            description="Legal business name as registered"
            required
          />

          <Select
            label="Business Type"
            placeholder="Select business type"
            options={businessTypeOptions}
            value={formData?.businessType}
            onChange={(value) => handleInputChange('businessType', value)}
            error={errors?.businessType}
            description="Legal structure of your business"
            required
          />

          <Input
            label="Years in Business"
            type="number"
            min="0"
            max="100"
            placeholder="Enter years in business"
            value={formData?.yearsInBusiness}
            onChange={(e) => handleInputChange('yearsInBusiness', e?.target?.value)}
            error={errors?.yearsInBusiness}
            description="How long has your business been operating?"
            required
          />
        </div>
      )}

      {/* Volume Expectations */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="TrendingUp" size={20} />
          <span>Business Projections</span>
        </h3>

        <Select
          label="Expected Monthly Volume"
          placeholder="Select expected volume"
          options={expectedVolumeOptions}
          value={formData?.expectedVolume}
          onChange={(value) => handleInputChange('expectedVolume', value)}
          error={errors?.expectedVolume}
          description="Estimated monthly transaction volume you expect to generate"
          required
        />
      </div>

      {/* Agreement Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="FileText" size={20} />
          <span>Agent Agreements</span>
        </h3>

        <div className="space-y-3">
          <Checkbox
            label="I agree to the Agent Terms and Conditions"
            description="By checking this box, you agree to our agent partnership terms and commission structure"
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />

          <Checkbox
            label="I agree to the Privacy Policy"
            description="You consent to our collection and use of your personal and business data"
            checked={formData?.agreeToPrivacy}
            onChange={(e) => handleInputChange('agreeToPrivacy', e?.target?.checked)}
            error={errors?.agreeToPrivacy}
            required
          />

          <Checkbox
            label="I agree to comply with all regulatory requirements"
            description="You agree to maintain compliance with local money service business regulations and KYC/AML policies"
            checked={formData?.agreeToCompliance}
            onChange={(e) => handleInputChange('agreeToCompliance', e?.target?.checked)}
            error={errors?.agreeToCompliance}
            required
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-6">
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Business Verification
        </Button>
      </div>
    </form>
  );
};

export default AgentRegistrationForm;